import seedrandom from 'seedrandom';
import Node from '../node.js';
import { PARAM_TYPE, PARAM_UNITS } from '../connector.js';
import GeoUtils from '../geoUtils.js';
import Utils from '../utils.js';
import Utils2D from '../../../utils/utils2D.js';
import Utils3D from '../../utils/utils3D.js';

const UV_SCALE_MULT = 1.0;

export default class NodeRandomizeUVs extends Node
{
	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.GEOMETRY, 'geo', null);		

		this.addInput(PARAM_TYPE.STRING, 'random_seed', Math.floor(Math.random()*1000));

		this.addInput(PARAM_TYPE.BOOLEAN, 'randomize_u', true);
		this.addInput(PARAM_TYPE.FLOAT, 'jitter_u_amount', 0.25, { minValue: 0, maxValue: 1 });

		this.addInput(PARAM_TYPE.BOOLEAN, 'randomize_v', true);
		this.addInput(PARAM_TYPE.FLOAT, 'jitter_v_amount', 0.25, { minValue: 0, maxValue: 1 });
		this.addInputsToGroup('Position', ['randomize_u', 'jitter_u_amount', 'randomize_v', 'jitter_v_amount']);

		this.addInput(PARAM_TYPE.BOOLEAN, 'randomize_rotation', true);
		this.addInput(PARAM_TYPE.FLOAT, 'jitter_rotation_amount', 360, { minValue: 0, maxValue: 360, units: PARAM_UNITS.DEGREES });
		this.addInput(PARAM_TYPE.BOOLEAN, 'use_discrete_rotation_intervals_only', true);
		this.addInput(PARAM_TYPE.FLOAT, 'rotation_step', 90, { minValue: 1, maxValue: 180, units: PARAM_UNITS.DEGREES });
		this.addInputsToGroup('Rotation', ['randomize_rotation', 'jitter_rotation_amount', 'use_discrete_rotation_intervals_only', 'rotation_step']);

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');
	}

	getType()
	{
		return 'randomizeUVs';
	}

	randomizeUVsOn(pSrcMesh, randomGenerator, inputValues)
	{
        if(!pSrcMesh || pSrcMesh.getTotalIndices() === 0)
            return;

		let uJitter = 0;
		if(inputValues['randomize_u'])
			uJitter = (randomGenerator() - 0.5)/0.5*inputValues['jitter_u_amount'];
		let vJitter = 0;
		if(inputValues['randomize_v'])
			vJitter = (randomGenerator() - 0.5)/0.5*inputValues['jitter_v_amount'];

		const uvOffsetsInWorldSpace = true;
		let uvRotationDeg = 0;
		if(inputValues['randomize_rotation'])
		{
			if(inputValues['use_discrete_rotation_intervals_only'])
			{
				const iNumSteps = Math.floor(inputValues['jitter_rotation_amount']/inputValues['rotation_step']);
				const iStep = Math.round((randomGenerator() - 0.5)/0.5*iNumSteps);
				uvRotationDeg = iStep*inputValues['rotation_step'];
			}
			else
				uvRotationDeg = (randomGenerator() - 0.5)/0.5*inputValues['jitter_rotation_amount'];
		}

		let uvs = pSrcMesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
		if(!uvs)
			return;

		let indices = pSrcMesh.getIndices();

        let pRotatedUVs = BABYLON.Vector2.Zero();
		let pUVs = {
			x: null,
			y: null,
		};

		let processedUVs = new Set();

        let pointIndex;
        let subIndex;
		for(let index = 0; index < indices.length; index += 3)
		{
			for(subIndex = 0; subIndex < 3; subIndex++)
			{	
				pointIndex = indices[index + subIndex];
				if(processedUVs.has(pointIndex))
					continue;

				processedUVs.add(pointIndex);

    			pUVs.x = uvs[pointIndex*2 + 0];
	    		pUVs.y = uvs[pointIndex*2 + 1];
               			
				if(uvRotationDeg !== 0)
				{
					if(!uvOffsetsInWorldSpace)
					{
    					pUVs.x = pUVs.x*UV_SCALE_MULT - uJitter;
	    				pUVs.y = pUVs.y*UV_SCALE_MULT - vOffset;
					}
					Utils2D.rotatePointToRef(pUVs, uvRotationDeg, pRotatedUVs);
					pUVs.x = pRotatedUVs.x;
					pUVs.y = pRotatedUVs.y;
				}

    			uvs[pointIndex*2 + 0] = pUVs.x;
	    		uvs[pointIndex*2 + 1] = pUVs.y;

				if(uvOffsetsInWorldSpace)
				{
    				uvs[pointIndex*2 + 0] = uvs[pointIndex*2 + 0]*UV_SCALE_MULT - uJitter;
	    			uvs[pointIndex*2 + 1] = uvs[pointIndex*2 + 1]*UV_SCALE_MULT - vJitter;
				}
			}
		}

		pSrcMesh.setVerticesData(BABYLON.VertexBuffer.UVKind, uvs, false, false);
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{	
		let pGeoList = inputValues['geo'];
		if(!pGeoList)
			return null;

		let randomGenerator = seedrandom(inputValues['random_seed']);
		for(let pSrcMesh of pGeoList)
		{
			if(!pSrcMesh)
				continue;

			if(!Utils.getIsMeshObject(pSrcMesh))
				continue;

			this.randomizeUVsOn(pSrcMesh, randomGenerator, inputValues);
		}
	
		return pGeoList;
	}
}